import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaDownload, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import api from "../../services/api";
import toast from "react-hot-toast";

const WORKING_DAYS_PER_YEAR = 218;

export default function TjmCheck({ user, filters: parentFilters, organisation, businessUnit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [projectTjmOverrides, setProjectTjmOverrides] = useState({});

  const openUserPayment = (userId) => {
    const params = new URLSearchParams(location.search);
    params.set("user_modal_id", userId);
    params.set("user_modal_tab", "payment");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  useEffect(() => {
    fetchData();
  }, [user, parentFilters, businessUnit]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const userQuery = {
        role: ["admin", "user", "super_admin"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };

      if (user) {
        userQuery.manager_id = user._id;
        userQuery.managees_of_managees = parentFilters?.manageesOfManagees || false;
      }

      // Filter by business unit
      if (businessUnit._id !== "ALL") userQuery.business_unit_id = businessUnit._id;
      else if (organisation?._id && organisation._id !== "ALL") userQuery.organisation_id = organisation._id;

      const { data: usersData, ok } = await api.post(`/user/search`, userQuery);
      if (!ok || !usersData?.users) {
        setData([]);
        return;
      }

      const today = new Date();
      const filteredUsers = usersData.users.filter((u) => {
        if (u.starting_date && new Date(u.starting_date) > today) return false;
        return true;
      });

      const tjmData = filteredUsers.map((member) => {
        const isDaily = member.payment_type === "DAILY";
        const monthlyCost = isDaily ? 0 : (member.total_monthly_cost || member.payment_value || 0);
        const annualSalary = isDaily ? 0 : (member.brut_salary || monthlyCost * 12);
        
        // Daily cost
        let dailyCost = 0;
        if (member.tjm && member.tjm > 0) {
          dailyCost = member.tjm;
        } else if (monthlyCost > 0) {
          dailyCost = (monthlyCost * 12) / WORKING_DAYS_PER_YEAR;
        }

        const tjms = member.tjms || 0;
        const marginAbsolute = tjms - dailyCost;
        const marginPercent = tjms > 0 ? ((tjms - dailyCost) / tjms) * 100 : 0;

        // For freelancers, show if they're daily or monthly
        let contractDisplay = member.contract;
        if (member.contract === "FREELANCE") {
          contractDisplay = member.payment_type === "DAILY" ? "FREELANCE DAILY" : "FREELANCE MONTHLY";
        }

        return {
          _id: member._id,
          name: member.name,
          avatar: member.avatar,
          contract: member.contract,
          contractDisplay,
          position: member.position,
          organisation_name: member.organisation_name,
          annualSalary,
          monthlyCost,
          dailyCost,
          tjms,
          marginAbsolute,
          marginPercent,
        };
      });

      setData(tjmData);

      // Fetch project TJM overrides for all users
      const userIds = tjmData.map((u) => u._id);
      if (userIds.length > 0) {
        const { data: overridesData, ok: overridesOk } = await api.post(`/project/tjm-overrides-by-users`, { userIds });
        if (overridesOk && overridesData) {
          setProjectTjmOverrides(overridesData);
        }
      }
    } catch (error) {
      console.error("Error fetching TJM data:", error);
      toast.error("Error fetching TJM data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-300" />;
    return sortConfig.direction === "asc" ? <FaSortUp className="text-blue-500" /> : <FaSortDown className="text-blue-500" />;
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    if (typeof aVal === "string") {
      return sortConfig.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  const exportToCSV = () => {
    if (data.length === 0) {
      toast.error("No data to export");
      return;
    }

    const csvRows = [
      ["Name", "Contract", "Annual €", "Monthly €", "Daily €", "TJM Sell €", "Margin %", "Project Overrides"],
    ];

    sortedData.forEach((item) => {
      const overrides = projectTjmOverrides[item._id] || [];
      const overridesStr = overrides.map((o) => `${o.projectName}: ${Math.round(o.tjms)}€`).join("; ");
      
      csvRows.push([
        item.name,
        item.contract || "",
        Math.round(item.annualSalary),
        Math.round(item.monthlyCost),
        Math.round(item.dailyCost),
        Math.round(item.tjms),
        item.marginPercent.toFixed(1),
        overridesStr || "-",
      ]);
    });

    const csvContent = csvRows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `tjm-check-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported successfully");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={exportToCSV}
          disabled={data.length === 0}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <FaDownload />
          Export CSV
        </button>
      </div>

      {sortedData.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-1">Person {getSortIcon("name")}</div>
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contract</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("annualSalary")}>
                    <div className="flex items-center justify-end gap-1">Annual € {getSortIcon("annualSalary")}</div>
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("monthlyCost")}>
                    <div className="flex items-center justify-end gap-1">Monthly € {getSortIcon("monthlyCost")}</div>
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("dailyCost")}>
                    <div className="flex items-center justify-end gap-1">Daily € {getSortIcon("dailyCost")}</div>
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("tjms")}>
                    <div className="flex items-center justify-end gap-1">TJM Sell {getSortIcon("tjms")}</div>
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort("marginPercent")}>
                    <div className="flex items-center justify-end gap-1">Margin {getSortIcon("marginPercent")}</div>
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project Overrides</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedData.map((item) => {
                  const overrides = projectTjmOverrides[item._id] || [];
                  
                  return (
                    <tr key={item._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openUserPayment(item._id)}>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2">
                          <img src={item.avatar} alt={item.name} className="w-6 h-6 rounded-full object-cover" />
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[120px]">{item.name}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          item.contract === "CDI" ? "bg-green-100 text-green-800" :
                          item.contract === "CDD" ? "bg-blue-100 text-blue-800" :
                          item.contract === "FREELANCE" ? "bg-orange-100 text-orange-800" :
                          "bg-gray-100 text-gray-800"
                        }`}>
                          {item.contract === "FREELANCE" ? "FL" : item.contract}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-right text-gray-700">
                        {item.annualSalary > 0 ? Math.round(item.annualSalary).toLocaleString() : "-"}
                      </td>
                      <td className="px-2 py-2 text-xs text-right text-gray-700">
                        {item.monthlyCost > 0 ? Math.round(item.monthlyCost).toLocaleString() : "-"}
                      </td>
                      <td className="px-2 py-2 text-xs text-right text-gray-700">
                        {item.dailyCost > 0 ? Math.round(item.dailyCost).toLocaleString() : "-"}
                      </td>
                      <td className="px-2 py-2 text-xs text-right text-gray-900 font-medium">
                        {item.tjms > 0 ? Math.round(item.tjms).toLocaleString() : <span className="text-red-500">-</span>}
                      </td>
                      <td className="px-2 py-2 text-xs text-right">
                        {item.tjms > 0 && item.dailyCost > 0 ? (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                            item.marginPercent >= 30 ? "bg-green-100 text-green-800" :
                            item.marginPercent >= 15 ? "bg-yellow-100 text-yellow-800" :
                            "bg-red-100 text-red-800"
                          }`}>
                            {item.marginPercent.toFixed(0)}%
                          </span>
                        ) : "-"}
                      </td>
                      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                        {overrides.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {overrides.map((o) => {
                              const isAbove = o.tjms >= item.tjms;
                              const overrideMargin = o.tjms > 0 && item.dailyCost > 0 ? ((o.tjms - item.dailyCost) / o.tjms) * 100 : 0;
                              return (
                                <a
                                  key={o.projectId}
                                  href={`/project/${o.projectId}`}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border ${
                                    isAbove 
                                      ? "bg-green-50 border-green-200 hover:bg-green-100" 
                                      : "bg-red-50 border-red-200 hover:bg-red-100"
                                  }`}
                                  title={`${o.projectName}: ${Math.round(o.tjms)}€ (default: ${Math.round(item.tjms)}€)`}
                                >
                                  <span className="text-gray-700 truncate max-w-[80px]">{o.projectName}</span>
                                  <span className={`font-medium ${isAbove ? "text-green-700" : "text-red-700"}`}>
                                    {Math.round(o.tjms)}€ ({overrideMargin.toFixed(0)}%)
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center p-8 text-gray-500">No data found</div>
      )}
    </div>
  );
}
